// src/pages/settings/sms/components/TriggersPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, RefreshCw, Zap, Edit2, Check, X, AlertCircle } from 'lucide-react';
import {
  getSmsTriggers,
  createSmsTrigger,
  updateSmsTrigger,
  deleteSmsTrigger,
  getTriggerOptions,
} from '../../../../services/api/smsConfig';
import { useAlert } from '../../../../contexts/AlertContext';
import TriggerFormModal from './TriggerFormModal';
import styles from './TriggersPanel.module.css';

const TriggersPanel = ({ config }) => {
  const { success, error: showError } = useAlert();
  const [triggers, setTriggers] = useState([]);
  const [triggerOptions, setTriggerOptions] = useState({ orderStatuses: [], courierStatuses: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTrigger, setEditTrigger] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const smsLength = config?.gateway?.smsLength || 160;

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      const [tRes, opRes] = await Promise.all([getSmsTriggers(), getTriggerOptions()]);
      setTriggers(tRes.triggers || []);
      setTriggerOptions({ orderStatuses: opRes.orderStatuses || [], courierStatuses: opRes.courierStatuses || [] });
    } catch (err) {
      showError('Failed to load triggers');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (data) => {
    try {
      await createSmsTrigger(data);
      success('Trigger created');
      setShowForm(false);
      load();
    } catch (err) {
      showError(err.message || 'Failed to create trigger');
    }
  };

  const handleUpdate = async (data) => {
    try {
      await updateSmsTrigger(editTrigger.id, data);
      success('Trigger updated');
      setEditTrigger(null);
      load();
    } catch (err) {
      showError(err.message || 'Failed to update trigger');
    }
  };

  const handleToggleActive = async (trigger) => {
    try {
      await updateSmsTrigger(trigger.id, { isActive: !trigger.isActive });
      load();
    } catch (err) {
      showError('Failed to update trigger');
    }
  };

  const handleDelete = async (id) => {
    try {
      setDeletingId(id);
      await deleteSmsTrigger(id);
      success('Trigger deleted');
      load();
    } catch (err) {
      showError(err.message || 'Failed to delete trigger');
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusLabel = (trigger) => {
    if (trigger.eventType === 'orderStatus') {
      const s = triggerOptions.orderStatuses.find(o => o.id === trigger.triggerId);
      return s ? `Order Status → ${s.label}` : `Order Status #${trigger.triggerId}`;
    }
    const s = triggerOptions.courierStatuses.find(c => c.id === trigger.triggerId);
    if (s) return `Courier Status → ${s.statusLabel || s.statusCode} (${s.courierCompany?.name || ''})`;
    return `Courier Status #${trigger.triggerId}`;
  };

  if (isLoading) {
    return (
      <div className={styles.panel}>
        <div className={styles.loadingRow}><div className={styles.spinner} /><span>Loading triggers...</span></div>
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <div>
          <h3 className={styles.panelTitle}>SMS Triggers</h3>
          <p className={styles.panelDesc}>Define which events should send an SMS and what message to send.</p>
        </div>
        <button className={styles.primaryBtn} onClick={() => setShowForm(true)}>
          <Plus size={16} /> Add Trigger
        </button>
      </div>

      {triggers.length === 0 ? (
        <div className={styles.empty}>
          <Zap size={40} className={styles.emptyIcon} />
          <p>No triggers configured yet. Add one to start sending SMS notifications.</p>
        </div>
      ) : (
        <div className={styles.triggerList}>
          {triggers.map(trigger => (
            <div key={trigger.id} className={`${styles.triggerCard} ${!trigger.isActive ? styles.inactive : ''}`}>
              <div className={styles.triggerMeta}>
                <span className={`${styles.eventBadge} ${trigger.eventType === 'orderStatus' ? styles.orderBadge : styles.courierBadge}`}>
                  {trigger.eventType === 'orderStatus' ? 'Order' : 'Courier'}
                </span>
                <span className={styles.triggerLabel}>{getStatusLabel(trigger)}</span>
              </div>
              <div className={styles.triggerMessage}>{trigger.message}</div>
              <div className={styles.triggerFooter}>
                <span className={styles.charCount}>{trigger.message.length} / {smsLength} chars</span>
                <div className={styles.triggerActions}>
                  <button
                    className={`${styles.iconBtn} ${trigger.isActive ? styles.activeBtn : styles.inactiveBtn}`}
                    onClick={() => handleToggleActive(trigger)}
                    title={trigger.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {trigger.isActive ? <Check size={14} /> : <X size={14} />}
                  </button>
                  <button className={styles.iconBtn} onClick={() => setEditTrigger(trigger)} title="Edit">
                    <Edit2 size={14} />
                  </button>
                  <button
                    className={`${styles.iconBtn} ${styles.deleteBtn}`}
                    onClick={() => handleDelete(trigger.id)}
                    disabled={deletingId === trigger.id}
                    title="Delete"
                  >
                    {deletingId === trigger.id ? <RefreshCw size={14} className={styles.spin} /> : <Trash2 size={14} />}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(showForm || editTrigger) && (
        <TriggerFormModal
          trigger={editTrigger}
          triggerOptions={triggerOptions}
          smsLength={smsLength}
          onSubmit={editTrigger ? handleUpdate : handleCreate}
          onClose={() => { setShowForm(false); setEditTrigger(null); }}
        />
      )}
    </div>
  );
};

export default TriggersPanel;